import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  buildReportDoc,
  isFullHtmlDoc,
  isHtmlContent,
  markdownToHtml,
} from "@/lib/reportDoc";
import { sanitizeReportDocument } from "@/lib/sanitize";

/*
  A digest, readable by anybody holding the link. NO SIGN-IN.

  Mirrors PublicReportPage.jsx exactly, for digests instead of reports — see
  that file's header comment for the full reasoning shared by both: why this
  route sits outside ProtectedRoute in App.js, why it fetches the public
  view-digest edge function with a plain `fetch` rather than going through
  the Supabase client (which would attach a session if the reader happens to
  be signed in, making the page behave differently for them than for the
  recipient the link was actually sent to) or RLS (no policy can admit an
  anonymous read without also permitting a full LIST), and why rendering
  reuses buildReportDoc/REPORT_CSS/the same sanitizer — one renderer, so a
  shared digest and the in-app one cannot drift apart.
*/

const IFRAME_SANDBOX = "allow-popups allow-popups-to-escape-sandbox";

const FUNCTIONS_BASE = `${process.env.REACT_APP_SUPABASE_URL || ""}/functions/v1`;

const Frame = ({ children }) => (
  <div className="min-h-screen bg-[#EEF1F0] flex items-center justify-center px-4">
    <div className="max-w-lg w-full bg-white border border-border rounded-sm p-8 text-center">
      {children}
    </div>
  </div>
);

// "2026-08-26" -> "26 Aug 2026". Split rather than passed to new Date(): a
// plain calendar date parsed as a string is read as UTC and rolls back a day
// east of Greenwich (see lib/localDate.js's use elsewhere in this app).
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtDay = (iso) => {
  const [y, m, d] = String(iso || "").split("-").map(Number);
  if (!y || !m || !d) return "";
  return `${d} ${MONTHS[m - 1]} ${y}`;
};

export default function PublicDigestPage() {
  const { token } = useParams();
  const [state, setState] = useState({ status: "loading", digest: null });

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(`${FUNCTIONS_BASE}/view-digest?t=${encodeURIComponent(token || "")}`, {
          headers: { accept: "application/json" },
        });
        if (cancelled) return;
        if (!res.ok) {
          setState({ status: res.status === 404 ? "not_found" : "error", digest: null });
          return;
        }
        setState({ status: "ready", digest: await res.json() });
      } catch {
        if (!cancelled) setState({ status: "error", digest: null });
      }
    })();

    return () => { cancelled = true; };
  }, [token]);

  const html = useMemo(() => {
    const d = state.digest;
    if (!d) return "";
    const content = d.content || "";

    // Same three shapes a stored report can take, the same branch
    // PublicReportPage/ReportsPage use: a complete HTML document, an HTML
    // fragment written against REPORT_CSS's classes, or markdown from before
    // the format changed.
    if (isFullHtmlDoc(content)) return sanitizeReportDocument(content);

    const period = d.week_start && d.week_end ? `${fmtDay(d.week_start)} – ${fmtDay(d.week_end)}` : "";
    const generatedAt = d.generated_at ? new Date(d.generated_at) : null;
    const label = d.scope_label || "Whole portfolio";

    return sanitizeReportDocument(
      buildReportDoc({
        title: "Weekly Digest",
        eyebrow: "NG-IT Initiative Board",
        subtitle: label,
        meta: [
          { label: "Covering", value: period || "—" },
          { label: "Generated", value: generatedAt ? generatedAt.toLocaleString() : "—" },
        ],
        bodyHtml: isHtmlContent(content) ? content : markdownToHtml(content),
        footer: "Source: NG-IT Initiative Board — generated from initiatives, projects, tasks and leaves. "
          + "Read-only snapshot shared by link.",
      }),
    );
  }, [state.digest]);

  if (state.status === "loading") {
    return (
      <div className="min-h-screen bg-[#EEF1F0] flex items-center justify-center">
        <div className="trk-uppercase-label">Loading digest…</div>
      </div>
    );
  }

  if (state.status === "not_found") {
    return (
      <Frame>
        <div className="trk-uppercase-label mb-2">NG-IT Initiative Board</div>
        <h1 className="font-display text-xl font-bold mb-3">This link is not valid</h1>
        <p className="text-sm text-zinc-600">
          The digest this link points to could not be found. It may have been deleted, or the link
          may have been broken in delivery — mail clients often wrap a long URL across two lines,
          and only the first half arrives.
        </p>
        <p className="text-sm text-zinc-600 mt-3">
          Ask whoever sent it to share the link again.
        </p>
      </Frame>
    );
  }

  if (state.status === "error") {
    return (
      <Frame>
        <div className="trk-uppercase-label mb-2">NG-IT Initiative Board</div>
        <h1 className="font-display text-xl font-bold mb-3">The digest could not be loaded</h1>
        <p className="text-sm text-zinc-600">
          Something went wrong reaching the digest. Reloading the page usually clears it.
        </p>
      </Frame>
    );
  }

  return (
    <div className="min-h-screen bg-[#EEF1F0]">
      <iframe
        title="Digest"
        sandbox={IFRAME_SANDBOX}
        srcDoc={html}
        data-testid="public-digest-frame"
        className="w-full min-h-screen border-0 bg-[#EEF1F0]"
      />
    </div>
  );
}
